import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import { useToast } from "../hooks/use-toast";
import { Check, Store, Truck, Loader2, Building2, Map, Snowflake, ShieldCheck, Layers } from "lucide-react";
import { DELIVERY_ZONES, DELIVERY_EXTRAS, WARRANTY_EXTENSIONS } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { Building2, Map };

const Checkout = () => {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [shippingMethod, setShippingMethod] = useState("store");
  const [payment, setPayment] = useState("store");
  const [placed, setPlaced] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orderRef, setOrderRef] = useState("");
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "",
    address: "", postalCode: "", city: "Marseille", notes: "",
  });
  // New: delivery options
  const [deliveryZone, setDeliveryZone] = useState("marseille"); // "marseille" | "dep13"
  const [americanFridge, setAmericanFridge] = useState(false);
  const [floors, setFloors] = useState(0); // extra floors without elevator
  // New: warranty extension
  const [warranty, setWarranty] = useState("none");

  const selectedZone = DELIVERY_ZONES.find((z) => z.id === deliveryZone) || DELIVERY_ZONES[0];
  const selectedWarranty = WARRANTY_EXTENSIONS.find((w) => w.id === warranty) || WARRANTY_EXTENSIONS[0];

  const shipping = useMemo(() => {
    if (shippingMethod === "store") return 0;
    let s = selectedZone.price;
    if (americanFridge) s += DELIVERY_EXTRAS.americanFridgeSurcharge;
    if (floors > 0) s += floors * DELIVERY_EXTRAS.perFloorNoElevator;
    return s;
  }, [shippingMethod, selectedZone, americanFridge, floors]);

  const warrantyFee = selectedWarranty.price;
  const total = subtotal + shipping + warrantyFee;

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const optionsSummary = [];
      if (shippingMethod === "home") {
        optionsSummary.push(`Zone : ${selectedZone.label} (${selectedZone.price}€)`);
        if (americanFridge) optionsSummary.push(`Supplément frigo américain : +${DELIVERY_EXTRAS.americanFridgeSurcharge}€`);
        if (floors > 0) optionsSummary.push(`Étages sans ascenseur : ${floors} × ${DELIVERY_EXTRAS.perFloorNoElevator}€ = ${floors * DELIVERY_EXTRAS.perFloorNoElevator}€`);
      }
      if (selectedWarranty.months > 0) optionsSummary.push(`Extension garantie ${selectedWarranty.months} mois : +${selectedWarranty.price}€`);

      const enrichedNotes = [form.notes, optionsSummary.length ? `\n\n— Options choisies —\n${optionsSummary.join("\n")}` : ""].filter(Boolean).join("");

      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        address: shippingMethod === "home" ? form.address : "",
        postalCode: shippingMethod === "home" ? form.postalCode : "",
        city: shippingMethod === "home" ? form.city : "",
        shippingMethod,
        paymentMethod: payment,
        items: items.map((it) => ({
          id: it.id,
          name: it.name,
          brand: it.brand || "",
          price: it.price,
          qty: it.qty,
        })),
        subtotal,
        shipping,
        total,
        notes: enrichedNotes,
      };
      const { data } = await axios.post(`${API}/orders`, payload, { timeout: 20000 });
      setOrderRef(data.reference);
      setPlaced(true);
      toast({ title: "Commande envoyée !", description: `Référence ${data.reference} — nous vous contactons rapidement.` });
      setTimeout(() => clear(), 1000);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Impossible d'envoyer la commande. Merci de réessayer ou de nous appeler.";
      toast({ title: "Erreur", description: msg, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (placed) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <div className="h-16 w-16 mx-auto rounded-full bg-green-100 text-green-600 flex items-center justify-center">
          <Check className="h-8 w-8" />
        </div>
        <h1 className="mt-5 text-2xl md:text-3xl font-extrabold text-[#0b2960]">Merci pour votre commande !</h1>
        {orderRef && (
          <div className="mt-3 inline-block bg-[#0b2960] text-white rounded-md px-4 py-2 text-sm font-bold tracking-wider">
            Référence : {orderRef}
          </div>
        )}
        <p className="text-neutral-600 mt-3">Nous avons bien reçu votre commande. Notre équipe vous contactera au plus vite au numéro fourni pour confirmer les détails et organiser {shippingMethod === "store" ? "le retrait en magasin" : "la livraison"}.</p>
        <div className="mt-4 inline-block bg-neutral-100 rounded-md px-4 py-2 text-sm text-neutral-700">
          Vous recevrez également un email de confirmation à l'adresse indiquée.
        </div>
        <div className="mt-6 flex gap-3 justify-center">
          <Button onClick={() => navigate("/")} className="bg-[#0b2960] hover:bg-[#081e47] h-11 px-6">Retour à l'accueil</Button>
          <Button onClick={() => navigate("/boutique")} variant="outline" className="h-11 px-6 border-[#0b2960] text-[#0b2960] hover:bg-[#0b2960] hover:text-white">Continuer mes achats</Button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-bold text-[#0b2960]">Votre panier est vide.</h1>
        <Button onClick={() => navigate("/boutique")} className="mt-4 bg-[#0b2960] hover:bg-[#081e47]">Voir la boutique</Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-2xl md:text-3xl font-extrabold text-[#0b2960]">Finaliser ma commande</h1>

      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_380px] gap-6 mt-6">
        <div className="space-y-6">
          <section className="bg-white border border-neutral-200 rounded-lg p-6">
            <h2 className="font-bold text-[#0b2960] mb-4">1. Vos coordonnées</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Prénom *</Label><Input required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className="mt-1" /></div>
              <div><Label>Nom *</Label><Input required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className="mt-1" /></div>
              <div><Label>Email *</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
              <div><Label>Téléphone *</Label><Input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" placeholder="06 12 34 56 78" /></div>
            </div>
          </section>

          <section className="bg-white border border-neutral-200 rounded-lg p-6">
            <h2 className="font-bold text-[#0b2960] mb-4">2. Mode de livraison</h2>
            <RadioGroup value={shippingMethod} onValueChange={setShippingMethod} className="space-y-3">
              <label className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer ${shippingMethod === "home" ? "border-[#0b2960] bg-[#0b2960]/5" : "border-neutral-200"}`}>
                <RadioGroupItem value="home" id="m-home" />
                <Truck className="h-5 w-5 text-[#0b2960]" />
                <div className="flex-1">
                  <div className="font-semibold text-neutral-800">Livraison à domicile</div>
                  <div className="text-xs text-neutral-500">Marseille & Bouches-du-Rhône · sous 48h</div>
                </div>
                <span className="font-bold text-[#0b2960]">à partir de 30 €</span>
              </label>
              <label className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer ${shippingMethod === "store" ? "border-[#0b2960] bg-[#0b2960]/5" : "border-neutral-200"}`}>
                <RadioGroupItem value="store" id="m-store" />
                <Store className="h-5 w-5 text-[#0b2960]" />
                <div className="flex-1">
                  <div className="font-semibold text-neutral-800">Retrait en magasin</div>
                  <div className="text-xs text-neutral-500">304 Av. de la Capelette, 13010 Marseille</div>
                </div>
                <span className="font-bold text-green-600">Gratuit</span>
              </label>
            </RadioGroup>

            {shippingMethod === "home" && (
              <>
                <div className="mt-5">
                  <h3 className="text-sm font-bold text-[#0b2960] mb-2">Zone de livraison</h3>
                  <RadioGroup value={deliveryZone} onValueChange={setDeliveryZone} className="grid sm:grid-cols-2 gap-3">
                    {DELIVERY_ZONES.map((z) => {
                      const Icon = ICONS[z.icon] || Truck;
                      const active = deliveryZone === z.id;
                      return (
                        <label key={z.id} className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer ${active ? "border-[#0b2960] bg-[#0b2960]/5" : "border-neutral-200"}`}>
                          <RadioGroupItem value={z.id} className="mt-1" />
                          <Icon className="h-5 w-5 text-[#0b2960] mt-0.5" />
                          <div className="flex-1">
                            <div className="font-semibold text-neutral-800 text-sm">{z.label}</div>
                            <div className="text-xs text-neutral-500">{z.sub}</div>
                          </div>
                          <span className="font-bold text-[#0b2960]">{z.price} €</span>
                        </label>
                      );
                    })}
                  </RadioGroup>
                </div>

                <div className="mt-5 space-y-3">
                  <label className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer ${americanFridge ? "border-[#0b2960] bg-[#0b2960]/5" : "border-neutral-200"}`}>
                    <Checkbox checked={americanFridge} onCheckedChange={setAmericanFridge} />
                    <Snowflake className="h-5 w-5 text-[#0b2960]" />
                    <div className="flex-1">
                      <div className="font-semibold text-neutral-800 text-sm">Frigo américain / grandes dimensions</div>
                      <div className="text-xs text-neutral-500">Supplément pour la manutention d'un réfrigérateur volumineux</div>
                    </div>
                    <span className="font-bold text-[#0b2960]">+{DELIVERY_EXTRAS.americanFridgeSurcharge} €</span>
                  </label>

                  <div className={`flex items-center gap-3 p-3 border-2 rounded-lg ${floors > 0 ? "border-[#0b2960] bg-[#0b2960]/5" : "border-neutral-200"}`}>
                    <Layers className="h-5 w-5 text-[#0b2960]" />
                    <div className="flex-1">
                      <div className="font-semibold text-neutral-800 text-sm">Étages sans ascenseur</div>
                      <div className="text-xs text-neutral-500">+{DELIVERY_EXTRAS.perFloorNoElevator} € par étage au-delà du RDC</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => setFloors(Math.max(0, floors - 1))} className="h-8 w-8 rounded-md border border-neutral-300 text-neutral-700 hover:bg-neutral-100 font-bold">−</button>
                      <span className="w-8 text-center font-bold">{floors}</span>
                      <button type="button" onClick={() => setFloors(floors + 1)} className="h-8 w-8 rounded-md border border-neutral-300 text-neutral-700 hover:bg-neutral-100 font-bold">+</button>
                    </div>
                    <span className="font-bold text-[#0b2960] min-w-[54px] text-right">{floors * DELIVERY_EXTRAS.perFloorNoElevator} €</span>
                  </div>
                </div>

                <div className="mt-5 grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2"><Label>Adresse *</Label><Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" /></div>
                  <div><Label>Code postal *</Label><Input required value={form.postalCode} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} className="mt-1" /></div>
                  <div><Label>Ville *</Label><Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1" /></div>
                </div>
              </>
            )}
          </section>

          <section className="bg-white border border-neutral-200 rounded-lg p-6">
            <h2 className="font-bold text-[#0b2960] mb-1">3. Extension de garantie <span className="text-xs font-normal text-neutral-500">(optionnelle)</span></h2>
            <p className="text-xs text-neutral-500 mb-4">En complément de la garantie constructeur incluse avec votre produit.</p>
            <RadioGroup value={warranty} onValueChange={setWarranty} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {WARRANTY_EXTENSIONS.map((w) => {
                const active = warranty === w.id;
                return (
                  <label key={w.id} className={`flex flex-col items-start gap-1 p-3 border-2 rounded-lg cursor-pointer ${active ? "border-[#0b2960] bg-[#0b2960]/5" : "border-neutral-200"}`}>
                    <div className="flex items-center gap-2 w-full">
                      <RadioGroupItem value={w.id} />
                      {w.months > 0 ? <ShieldCheck className="h-4 w-4 text-[#0b2960]" /> : null}
                      <div className="font-semibold text-neutral-800 text-sm">{w.label}</div>
                    </div>
                    <div className="text-lg font-black text-[#e11d24] ml-6">{w.price === 0 ? "0 €" : `+${w.price} €`}</div>
                  </label>
                );
              })}
            </RadioGroup>
          </section>

          <section className="bg-white border border-neutral-200 rounded-lg p-6">
            <h2 className="font-bold text-[#0b2960] mb-4">4. Paiement</h2>
            <RadioGroup value={payment} onValueChange={setPayment} className="space-y-3">
              {[
                { id: "store", title: "Payer sur place au magasin", text: "304 Av. de la Capelette, 13010 Marseille", icon: Store },
                { id: "delivery", title: "Payer à la livraison", text: "Espèces ou carte bancaire à la livraison", icon: Truck },
              ].map((p) => (
                <label key={p.id} className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer ${payment === p.id ? "border-[#0b2960] bg-[#0b2960]/5" : "border-neutral-200"}`}>
                  <RadioGroupItem value={p.id} />
                  <p.icon className="h-5 w-5 text-[#0b2960]" />
                  <div className="flex-1">
                    <div className="font-semibold text-neutral-800">{p.title}</div>
                    <div className="text-xs text-neutral-500">{p.text}</div>
                  </div>
                </label>
              ))}
            </RadioGroup>
            <div className="mt-3 text-xs text-neutral-500 bg-neutral-50 border border-neutral-200 rounded-md p-3">
              Le paiement s'effectue en <b>une seule fois</b>, directement au magasin ou à la livraison. Aucune information bancaire n'est demandée en ligne.
            </div>
          </section>

          <section className="bg-white border border-neutral-200 rounded-lg p-6">
            <h2 className="font-bold text-[#0b2960] mb-4">5. Note pour le vendeur (facultatif)</h2>
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Une précision sur votre commande, horaires de disponibilité, étage sans ascenseur, etc." />
          </section>
        </div>

        <aside className="bg-white border border-neutral-200 rounded-lg p-6 h-fit sticky top-40">
          <h3 className="font-bold text-[#0b2960] text-lg mb-4">Votre commande</h3>
          <ul className="divide-y divide-neutral-200 mb-4">
            {items.map((it) => (
              <li key={it.id} className="py-3 flex items-center gap-3">
                <div className="h-14 w-14 rounded-md bg-neutral-100 overflow-hidden shrink-0 flex items-center justify-center">
                  {it.image ? (
                    <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[8px] uppercase tracking-widest text-neutral-400">Photo</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-neutral-600 truncate">{it.name}</div>
                  <div className="text-xs text-neutral-500">Qté : {it.qty}</div>
                </div>
                <div className="text-sm font-bold text-neutral-800">{(it.price * it.qty).toFixed(2)} €</div>
              </li>
            ))}
          </ul>
          <div className="space-y-1 text-sm text-neutral-700 border-t border-neutral-200 pt-3">
            <div className="flex justify-between"><span>Sous-total</span><span className="font-semibold">{subtotal.toFixed(2)} €</span></div>
            {shippingMethod === "home" && (
              <>
                <div className="flex justify-between"><span>Livraison ({selectedZone.label.replace("Livraison ", "")})</span><span className="font-semibold">{selectedZone.price.toFixed(2)} €</span></div>
                {americanFridge && (
                  <div className="flex justify-between text-xs text-neutral-500"><span>+ Supplément frigo américain</span><span>+{DELIVERY_EXTRAS.americanFridgeSurcharge.toFixed(2)} €</span></div>
                )}
                {floors > 0 && (
                  <div className="flex justify-between text-xs text-neutral-500"><span>+ {floors} étage{floors > 1 ? "s" : ""} sans ascenseur</span><span>+{(floors * DELIVERY_EXTRAS.perFloorNoElevator).toFixed(2)} €</span></div>
                )}
              </>
            )}
            {shippingMethod === "store" && (
              <div className="flex justify-between"><span>Retrait magasin</span><span className="font-semibold text-green-600">Gratuit</span></div>
            )}
            {selectedWarranty.months > 0 && (
              <div className="flex justify-between"><span>Extension garantie {selectedWarranty.months} mois</span><span className="font-semibold">+{selectedWarranty.price.toFixed(2)} €</span></div>
            )}
          </div>
          <div className="mt-3 pt-3 border-t border-neutral-200 flex justify-between items-baseline">
            <span className="font-bold">Total TTC</span>
            <span className="text-2xl font-black text-[#e11d24]">{total.toFixed(2)} €</span>
          </div>
          <Button type="submit" disabled={submitting} className="mt-4 w-full h-12 bg-[#e11d24] hover:bg-[#b8171d] font-bold text-base disabled:opacity-70">
            {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Envoi en cours...</>) : "Confirmer la commande"}
          </Button>
          <div className="mt-3 text-xs text-neutral-500 text-center">En cliquant, vous acceptez nos CGV</div>
        </aside>
      </form>
    </div>
  );
};

export default Checkout;
